import {
	adyenWebhook,
	getAccessibilityPreferences,
	getCountries,
	getFieldList,
	getMasterBootstrap,
	getMultiTexts,
	getPrivacyConsentData,
	getPropertyInfo,
	getPropertyInfoByCode,
	getReservation,
	getReservationByMagicToken,
	getReservationProfiles,
	getSettingList,
	getUIPropertiesByProperty,
	getVisitPurposes,
	updateReservationProfile,
	UpsertProfile,
	upsertProfileVerificationStatus,
	validateMagicAuth,
} from '../api';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { applyUiProperties } from '../../utils';
import type { RootState } from './../store';
import { stat } from 'fs';

interface DocumentMaster {
	result: string;
	documentName: string;
	boDocumentType: string;
}
const initialState = {
	loading: true,
	toast: false,
	token: sessionStorage.getItem('token'),
	confirmationNo: null,
	countries: null,
	fields: null,
	settings: null,
	reservation: null,
	propertyInfo: null,
	uiProperties: null,
	emailPrimary: null,
	phonePrimary: null,
	isPreCheckinDone: null,
	supportDocs: null,

	error: {
		fetchToken: null,
		fetchCountries: null,
		fetchFields: null,
		fetchSettingLists: null,
		fetchReservation: null,
		fetchPropertyInfo: null,
		fetchUIProperties: null,
		fetchReservationProfiles: null,
		updateReservation: null,
		fetchTexts: null,
		fetchPrivacyConsentData: null,
	},
	profiles: null,
	isPostComplete: null,
	multiTexts: null,
	privacyConsentData: null,
	propertyPrivacyConsentData: null,
	guestVerificationLazyLoad: false,
	visitPurposes: null,
	accessibilityPreferences: null,
} as any;

export const fetchToken = createAsyncThunk(
	'Profile/FetchToken',
	async (magicToken: string, { rejectWithValue }) => {
		try {
			const response = await validateMagicAuth(magicToken);
			const authToken = response?.data?.token;

			if (!authToken) {
				sessionStorage.removeItem('token');
				sessionStorage.removeItem('magicToken');
				return rejectWithValue({
					status: response?.status,
					message:
						response?.data?.message ??
						'Magic token is invalid or expired. Please regenerate link.',
				});
			}

			sessionStorage.setItem('token', authToken);
			sessionStorage.setItem('magicToken', magicToken);
			return authToken;
		} catch (error: any) {
			sessionStorage.removeItem('token');
			sessionStorage.removeItem('magicToken');
			return rejectWithValue({
				status: error?.response?.status,
				message:
					error?.response?.data?.message ??
					error?.message ??
					'Magic token validation failed.',
			});
		}
	}
);

export const fetchPropertyInfo = createAsyncThunk(
	'Profile/FetchPropertyInfo',
	async (token: string, thunkAPI) => {
		try {
			const response = await getPropertyInfo(token);
			if (response && response.data && response.data.data) {
				return response.data.data;
			} else {
				// If the response or its data property is not as expected, throw an error
				throw new Error('Invalid response structure');
			}
		} catch (error) {
			console.error('Error fetching property info:', error);
			throw error; // Re-throw the error to propagate it
		}
	}
);

export const fetchPropertyInfoByCode = createAsyncThunk(
	'Profile/FetchPropertyInfoByCode',
	async (propertyCode: string, thunkAPI) => {
		try {
			const response = await getPropertyInfoByCode(propertyCode);
			if (response && response.data && response.data.data) {
				return response.data.data;
			} else {
				// If the response or its data property is not as expected, throw an error
				throw new Error('Invalid response structure');
			}
		} catch (error) {
			console.error('Error fetching property info by code:', error);
			throw error; // Re-throw the error to propagate it
		}
	}
);

export const fetchUIProperties = createAsyncThunk(
	'Profile/FetchUIProperties',
	async (token: string, thunkAPI) => {
		try {
			/* eslint-disable no-debugger */
			// debugger;
			/* eslint-enable no-debugger */
			const response = await getUIPropertiesByProperty(token);
			const data = response?.data?.data ?? response?.data ?? null;
			if (data) {
				applyUiProperties(data);
			}
			return data;
		} catch (error: any) {
			console.error('Error fetching UI properties:', error);
			return thunkAPI.rejectWithValue(
				error?.response?.data?.message ?? 'Failed to fetch UI properties'
			);
		}
	},
	{
		condition: (token, { getState }) => {
			const { session } = getState() as any;
			if (session.uiProperties) {
				return false;
			}
		},
	}
);

export const fetchCountries = createAsyncThunk(
	'Profile/FetchCountries',
	async (token: string, thunkAPI) => {
		const response = await getCountries(token);
		return response?.data?.data;
	},
	{
		condition: (token, { getState }) => {
			const { session } = getState() as any;
			if (session.countries) {
				return false;
			}
		},
	}
);

export const fetchReservationProfiles = createAsyncThunk<
	any,
	{
		token: string;
		confirmationNo: string;
	}
>(
	'Profile/FetchReservationProfiles',
	async (reservationProfileParam, thunkAPI) => {
		const response = await getReservationProfiles(
			reservationProfileParam?.token,
			reservationProfileParam?.confirmationNo
		);
		return response?.data;
	}
);

export const fetchFields = createAsyncThunk(
	'Profile/FetchFields',
	async (token: string, thunkAPI) => {
		const response = await getFieldList(token);
		return response?.data?.data;
	},
	{
		condition: (token, { getState }) => {
			const { session } = getState() as any;
			if (session.fields) {
				return false;
			}
		},
	}
);
export const fetchVisitPurposes = createAsyncThunk(
	'Profile/GetVisitPurposes',
	async (token: string, thunkAPI) => {
		const response = await getVisitPurposes(token);
		return response?.data;
	},
	{
		condition: (token, { getState }) => {
			const { session } = getState() as any;
			if (session.visitPurposes) {
				return false;
			}
		},
	}
);
export const fetchAccessibilityPreferences = createAsyncThunk(
	'Profile/GetAccessibilityPreferences',
	async (token: string, thunkAPI) => {
		const response = await getAccessibilityPreferences(token);
		return response?.data;
	},
	{
		condition: (token, { getState }) => {
			const { session } = getState() as any;
			if (session.accessibilityPreferences) {
				return false;
			}
		},
	}
);
export const fetchSettingLists = createAsyncThunk(
	'Profile/SettingFields',
	async (token: string, thunkAPI) => {
		const response = await getSettingList(token);
		return response?.data?.data;
	},
	{
		condition: (token, { getState }) => {
			const { session } = getState() as any;
			if (session.settings) {
				return false;
			}
		},
	}
);
export const fetchMasterBootstrap = createAsyncThunk(
	'Profile/FetchMasterBootstrap',
	async (token: string, thunkAPI) => {
		const response = await getMasterBootstrap(token);
		return response?.data?.data;
	}
);
export const fetchReservation = createAsyncThunk<
	{
		addressLine1: string;
		adultCount: number;
		arrivalDate: string;
		childCount: number;
		city: string;
		createdDate: string;
		departureDate: string;
		firstName: string;
		gender: string;
		isPreCheckInDone: boolean;
		isPrimary: boolean;
		lastName: string;
		pincode: string;
		pmsConfirmationNo: string;
		rateCode: string;
		rateCodeAmount: number;
		reservationID: number;
		reservationNameID: number;
		roomTypeName: string;
		propertyName: string;
		roomNo: string;
		state: string;
		title: string;
		balanceAmount: string;
		totalAmount: string;
		printRate?: boolean;
		error?: string;
	},
	{ token: string; confirmationNo: string; propertyCode: string },
	{
		rejectValue: any;
	}
>('Profile/FetchReservation', async (reservationParam, _thunkAPI) => {
	const { token, confirmationNo, propertyCode } = reservationParam;
	const magicToken = sessionStorage.getItem('magicToken');
	if (
		propertyCode !== '' &&
		propertyCode !== null &&
		propertyCode !== undefined
	) {
		const response = await getReservationByMagicToken(magicToken);
		// if (response?.data?.data !== null && response?.data?.data !== undefined) { DUEIN -- status
		// 	console.log('reservation fetch');
		// 	const arrivalDate = response?.data?.data?.arrivalDate;

		// 	const today = new Date();
		// 	today.setHours(0, 0, 0, 0);
		// 	if (arrivalDate && new Date(arrivalDate) < today) {
		// 		console.log('arrivalDate is less than today');
		// 		return _thunkAPI.rejectWithValue({
		// 			message: 'Reservation has Expired.',
		// 			code: '301',
		// 			response: null,
		// 		});
		// 	}
		// }
		if (
			response?.data?.data !== null &&
			response?.data?.data !== undefined &&
			response?.data?.isPrecheckInAllowed === false
		) {
			return _thunkAPI.rejectWithValue({
				message: 'Reservation has Expired.',
				code: '301',
				response: null,
			});
		}
		if (response?.data?.data === null || response?.data?.result === false) {
			return _thunkAPI.rejectWithValue({
				message: 'Reservation not found.',
				code: '105',
				response: null,
			});
		}
		return response?.data?.data;
	}
});

export const callAdyenWebhook = createAsyncThunk<
	any,
	{ token: string; orderRef: string; redirectResult: string }
>('Profile/AdyenWebhook', async (model, _thunkAPI) => {
	const { token, orderRef, redirectResult } = model;
	const response = await adyenWebhook(token, orderRef, redirectResult);
	if (response?.data?.result !== true) {
		return _thunkAPI.rejectWithValue({
			message: response?.data?.message ?? 'Payment failed',
			code: '301',
			response: null,
		});
	}
	return response?.data;
});

export const updateReservation = createAsyncThunk<
	{
		result: boolean;
		profiles: object;
		reservation: {
			addressLine1: string;
			adultCount: number;
			arrivalDate: string;
			childCount: number;
			city: string;
			createdDate: string;
			departureDate: string;
			firstName: string;
			gender: string;
			isPreCheckInDone: boolean;
			isPrimary: boolean;
			lastName: string;
			pincode: string;
			pmsConfirmationNo: string;
			rateCode: string;
			rateCodeAmount: number;
			reservationID: number;
			reservationNameID: number;
			roomTypeName: string;
			propertyName: string;
			roomNo: string;
			state: string;
			title: string;
			printRate: string;
		};
	},
	{
		token: string;
		reservationID: number;
		profileID: number;
		pmsConfirmationNo: string;
		title: string;
		firstName: string;
		middleName: string;
		lastName: string;
		email: string;
		phoneNo_Res: string;
	}
>('Profile/UpdateReservation', async (reservationParam, _thunkAPI) => {
	const updateResponse = await updateReservationProfile(reservationParam);
	if (updateResponse?.data?.result !== true) {
		return _thunkAPI.rejectWithValue({
			message:
				updateResponse?.data?.message ??
				'Failed to update reservation profile.',
			code: '302',
			response: updateResponse?.data ?? null,
		});
	}
	const profilesResponse = await getReservationProfiles(
		reservationParam?.token,
		reservationParam?.pmsConfirmationNo
	);
	const reservationResponse = await getReservation(
		reservationParam?.token,
		reservationParam?.pmsConfirmationNo,
		''
	);
	return {
		result: true,
		profiles: profilesResponse?.data?.data,
		reservation: reservationResponse?.data?.data,
	};
});

export const fetchTexts = createAsyncThunk(
	'Master/FetchMultiTexts',
	async (payload: { token: string; language: string }, thunkAPI) => {
		const { token, language } = payload;
		const response = await getMultiTexts(token, language);
		return response?.data?.data;
	},
	{
		condition: (payload, { getState }) => {
			const { session } = getState() as any;
			if (session.multiTexts) {
				return false;
			}
		},
	}
);

export const fetchPrivacyConsentData = createAsyncThunk(
	'Master/FetchPrivacyConsentData',
	async (payload: { languageCode: string; groupName: string }, thunkAPI) => {
		const { languageCode, groupName } = payload;
		const response = await getPrivacyConsentData(languageCode, groupName);
		return response?.data?.data;
	},
	{
		condition: (payload, { getState }) => {
			const { session } = getState() as any;
			const { groupName } = payload;
			if (groupName === 'PropertyPrivacy') {
				if (session.propertyPrivacyConsentData) return false;
			} else {
				if (session.privacyConsentData) return false;
			}
		},
	}
);

export const upsertProfileVerificationStatusThunk = createAsyncThunk(
	'Profile/UpsertProfileVerificationStatus',
	async (
		payload: {
			token: string;
			profile: any;
		},
		{ rejectWithValue }
	) => {
		try {
			const response = await upsertProfileVerificationStatus(
				payload.token,
				payload.profile
			);
			return response?.data;
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message || 'Failed to update verification status'
			);
		}
	}
);

export const upsertProfileThunk = createAsyncThunk(
	'Profile/UpsertProfile',
	async (
		payload: {
			token: string;
			profile: any;
		},
		{ rejectWithValue }
	) => {
		try {
			const response = await UpsertProfile(payload.token, payload.profile);
			return response?.data;
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message || 'Failed to upsert profile'
			);
		}
	}
);

const sessionSlice = createSlice({
	name: 'session',
	initialState,
	reducers: {
		display: (state, action) => {
			state.loading = action?.payload;
		},
		displayToast: (state, action) => {
			state.toast = action?.payload;
		},
		clearError: (state) => {
			state.error = {
				fetchToken: null,
				fetchCountries: null,
				fetchFields: null,
				fetchSettingLists: null,
				fetchReservation: null,
				fetchPropertyInfo: null,
				fetchUIProperties: null,
				fetchReservationProfiles: null,
				updateReservation: null,
				fetchTexts: null,
				fetchPrivacyConsentData: null,
			};
		},
		resetPostComplete: (state) => {
			state.isPostComplete = false;
		},
		setEmail: (state, action) => {
			state.emailPrimary = action.payload;
		},
		setPhone: (state, action) => {
			state.phonePrimary = action.payload;
		},
		setGuestVerificationLazyLoad: (state, action) => {
			state.guestVerificationLazyLoad = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchReservation.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(fetchToken.fulfilled, (state, action) => {
			state.token = action?.payload;
			state.error.fetchToken = null;
		});

		builder.addCase(fetchToken.rejected, (state, action) => {
			state.loading = false;
			state.token = null;
			state.error.fetchToken = action?.payload ?? action?.error;
		});

		builder.addCase(fetchPropertyInfo.fulfilled, (state, action) => {
			state.propertyInfo = action?.payload;
			state.error.fetchPropertyInfo = null;
			//state.loading = false;
		});

		builder.addCase(fetchPropertyInfo.rejected, (state, action) => {
			state.error.fetchPropertyInfo = action?.error;
			state.loading = false;
		});
		builder.addCase(fetchPropertyInfoByCode.fulfilled, (state, action) => {
			state.propertyInfo = action?.payload;
			state.error.fetchPropertyInfoByCode = null;
			//state.loading = false;
		});

		builder.addCase(fetchPropertyInfoByCode.rejected, (state, action) => {
			state.error.fetchPropertyInfoByCode = action?.error;
			state.loading = false;
		});
		builder.addCase(fetchUIProperties.fulfilled, (state, action) => {
			state.uiProperties = action?.payload;
			state.error.fetchUIProperties = null;
		});

		builder.addCase(fetchUIProperties.rejected, (state, action) => {
			state.error.fetchUIProperties = action?.payload ?? action?.error;
		});
		builder.addCase(fetchCountries.fulfilled, (state, action) => {
			state.countries = action?.payload;
			state.error.fetchCountries = null;
		});

		builder.addCase(fetchCountries.rejected, (state, action) => {
			state.error.fetchCountries = action?.error;
		});

		builder.addCase(fetchFields.fulfilled, (state, action) => {
			state.fields = action?.payload;
			state.error.fetchFields = null;
		});

		builder.addCase(fetchFields.rejected, (state, action) => {
			state.error.fetchFields = action?.error;
		});

		builder.addCase(fetchSettingLists.fulfilled, (state, action) => {
			state.settings = action?.payload;
			state.error.fetchSettingLists = null;
		});

		builder.addCase(fetchSettingLists.rejected, (state, action) => {
			state.error.fetchSettingLists = action?.error;
		});

		builder.addCase(fetchReservation.fulfilled, (state, action) => {
			state.reservation = {
				AddressLine1: action?.payload?.addressLine1,
				AdultCount: action?.payload?.adultCount,
				ArrivalDate: action?.payload?.arrivalDate,
				ChildCount: action?.payload?.childCount,
				City: action?.payload?.city,
				CreatedDate: action?.payload?.createdDate,
				DepartureDate: action?.payload?.departureDate,
				FirstName: action?.payload?.firstName,
				Gender: action?.payload?.gender,
				IsPreCheckInDone: action?.payload?.isPreCheckInDone,
				IsPrimary: action?.payload?.isPrimary,
				LastName: action?.payload?.lastName,
				Pincode: action?.payload?.pincode,
				PmsConfirmationNo: action?.payload?.pmsConfirmationNo,
				RateCode: action?.payload?.rateCode,
				RateCodeAmount: action?.payload?.rateCodeAmount,
				ReservationID: action?.payload?.reservationID,
				ReservationNameID: action?.payload?.reservationNameID,
				RoomTypeName: action?.payload?.roomTypeName,
				PropertyName: action?.payload?.propertyName,
				RoomNo: action?.payload?.roomNo,
				State: action?.payload?.state,
				Title: action?.payload?.title,
				BalanceAmount: action?.payload?.balanceAmount,
				TotalAmount: action?.payload?.totalAmount,
				PrintRate: action?.payload?.printRate,
			};
			state.error.fetchReservation = null;
			state.loading = false;
			console.log(
				'Succeessss Msg--' + JSON.stringify(action?.payload?.printRate)
			);
		});

		builder.addCase(fetchReservation.rejected, (state, action) => {
			console.log('Error Msg--' + JSON.stringify(action?.error));
			state.loading = false;
			state.error.fetchReservation = action?.payload ?? action?.error;
		});

		builder.addCase(updateReservation.fulfilled, (state, action) => {
			state.error.updateReservation = null;
			state.isPostComplete = true;
			state.profiles = action?.payload?.profiles;
			state.reservation = {
				AddressLine1: action?.payload?.reservation?.addressLine1,
				AdultCount: action?.payload?.reservation?.adultCount,
				ArrivalDate: action?.payload?.reservation?.arrivalDate,
				ChildCount: action?.payload?.reservation?.childCount,
				City: action?.payload?.reservation?.city,
				CreatedDate: action?.payload?.reservation?.createdDate,
				DepartureDate: action?.payload?.reservation?.departureDate,
				FirstName: action?.payload?.reservation?.firstName,
				Gender: action?.payload?.reservation?.gender,
				IsPreCheckInDone: action?.payload?.reservation?.isPreCheckInDone,
				IsPrimary: action?.payload?.reservation?.isPrimary,
				LastName: action?.payload?.reservation?.lastName,
				Pincode: action?.payload?.reservation?.pincode,
				PmsConfirmationNo: action?.payload?.reservation?.pmsConfirmationNo,
				RateCode: action?.payload?.reservation?.rateCode,
				RateCodeAmount: action?.payload?.reservation?.rateCodeAmount,
				ReservationID: action?.payload?.reservation?.reservationID,
				ReservationNameID: action?.payload?.reservation?.reservationNameID,
				RoomTypeName: action?.payload?.reservation?.roomTypeName,
				PropertyName: action?.payload?.reservation?.propertyName,
				RoomNo: action?.payload?.reservation?.roomNo,
				State: action?.payload?.reservation?.state,
				Title: action?.payload?.reservation?.title,
				PrintRate: action?.payload?.reservation?.printRate,
			};
			console.log(
				'Succeessss Msg--' + JSON.stringify(action?.payload?.reservation)
			);
		});

		builder.addCase(updateReservation.rejected, (state, action) => {
			state.error.updateReservation = action?.error;
		});

		builder.addCase(fetchReservationProfiles.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(fetchReservationProfiles.fulfilled, (state, action) => {
			const payload = action?.payload;
			const profiles = action?.payload?.data ?? [];

			const totalAdults = state.reservation.AdultCount || 0;
			const totalChildren = state.reservation.ChildCount || 0;
			const totalGuests = totalAdults + totalChildren;

			if (profiles.length < totalGuests) {
				const missingCount = totalGuests - profiles.length;

				for (let i = 0; i < missingCount; i++) {
					const index = profiles.length; // current index in array
					const isChild = index >= totalAdults; // first adults, then children

					profiles.push({
						reservationID: profiles[0]?.reservationID || 0,
						pmsConfirmationNo: profiles[0]?.pmsConfirmationNo || '',
						profileID: 0, // Dummy ID
						lastName: '',
						firstName: '',
						dateOfBirth: null,
						gender: isChild ? 'Child' : '', // or 'M/F' if you want
						isPrimary: false,
						isChild: isChild, // ✅ explicit child flag
						placeholderName: isChild
							? `Child ${index + 1 - totalAdults}`
							: `Guest ${index + 1}`,
						// Add other required dummy fields
					});
				}
			}
			state.profiles = profiles;
			state.isPreCheckinDone =
				payload?.isPreCheckinDone ?? payload?.isPreCheckInDone ?? null;
			state.error.fetchReservationProfiles = null;
			state.loading = false;
		});

		builder.addCase(fetchReservationProfiles.rejected, (state, action) => {
			state.error.fetchReservationProfiles = action?.error;
			state.loading = false;
		});

		builder.addCase(fetchTexts.fulfilled, (state, action) => {
			state.multiTexts = action?.payload;
			state.error.fetchTexts = null;
		});
		builder.addCase(fetchTexts.rejected, (state, action) => {
			state.error.fetchTexts = action?.error;
		});
		builder.addCase(fetchPrivacyConsentData.fulfilled, (state, action) => {
			const groupName = action?.meta?.arg?.groupName;
			if (groupName === 'PropertyPrivacy') {
				state.propertyPrivacyConsentData = action?.payload;
			} else {
				state.privacyConsentData = action?.payload;
			}
			state.error.fetchPrivacyConsentData = null;
		});
		builder.addCase(fetchPrivacyConsentData.rejected, (state, action) => {
			state.error.fetchPrivacyConsentData = action?.error;
		});
		builder.addCase(callAdyenWebhook.rejected, (state, action) => {
			console.log('Error Msg--' + JSON.stringify(action?.error));
			state.loading = false;
			state.error.callAdyenWebhook = action?.payload ?? action?.error;
		});
		builder.addCase(callAdyenWebhook.fulfilled, (state, action) => {
			state.error.callAdyenWebhook = null;
			state.isPostComplete = true;
		});
		builder
			.addCase(upsertProfileVerificationStatusThunk.pending, (state) => {
				state.loading = true;
			})
			.addCase(
				upsertProfileVerificationStatusThunk.fulfilled,
				(state, action) => {
					state.loading = false;
					state.message = action.payload.message;
					state.result = action.payload.result;
				}
			)
			.addCase(
				upsertProfileVerificationStatusThunk.rejected,
				(state, action) => {
					state.loading = false;
					state.error = action.payload;
				}
			);
		builder.addCase(fetchVisitPurposes.fulfilled, (state, action) => {
			state.visitPurposes = action?.payload;
		});
		builder.addCase(
			fetchAccessibilityPreferences.fulfilled,
			(state, action) => {
				state.accessibilityPreferences = action?.payload;
			}
		);

		builder.addCase(fetchMasterBootstrap.rejected, (state, action) => {
			console.log('Error Msg--' + JSON.stringify(action?.error));
			state.loading = false;
		});
		builder.addCase(fetchMasterBootstrap.fulfilled, (state, action) => {
			state.fields = action?.payload?.fields;
			state.countries = action?.payload?.countries;
			state.settings = action?.payload?.settingsParameters;
			state.visitPurposes = action?.payload?.visitPurposes;
			state.accessibilityPreferences =
				action?.payload?.accessibilityPreferences;

			// state.documentTypes = action?.payload?.documents;
			/* eslint-disable no-debugger */
			debugger;
			if (
				action?.payload?.documents !== null &&
				action?.payload?.documents !== undefined
			) {
				const docs = action?.payload?.documents as DocumentMaster[];

				const map = new Map<string, DocumentMaster>();

				docs.forEach((item) => {
					const key = item.documentName;

					if (!map.has(key)) {
						// First entry
						map.set(key, { ...item });
					} else {
						// Merge BODocumentType
						const existing = map.get(key)!;

						const existingTypes = existing.boDocumentType
							? existing.boDocumentType.split(',')
							: [];

						const newTypes = item.boDocumentType
							? item.boDocumentType.split(',')
							: [];

						// Combine + remove duplicates
						const mergedTypes = Array.from(
							new Set([...existingTypes, ...newTypes])
						);

						existing.boDocumentType = mergedTypes.join(',');
					}
				});

				state.supportDocs = Array.from(map.values());
				console.log('state.supportDocs = ', state.supportDocs);
			} else {
				state.supportDocs = null;
			}
		});
	},
});

// export const {  } = sessionSlice.actions;
export const {
	display,
	clearError,
	resetPostComplete,
	displayToast,
	setEmail,
	setPhone,
	setGuestVerificationLazyLoad,
} = sessionSlice.actions;
export const selectSession = (state: RootState) => state.session;
//console.log('session-==>', JSON.stringify(selectSession));
export default sessionSlice.reducer;
